import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  Component,
  Input,
  type OnChanges,
  type OnDestroy,
  type OnInit,
  type SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  CountryConvertService,
  MerchantChangeService,
  PaginationHelperService,
  VTService,
} from '@odin/odin-core';
import type {
  PeblScheduleStatus,
  ScheduleItem,
  ScheduleListResponse,
} from '../models/pebl-payment';
import { MatDialog } from '@angular/material/dialog';
import { PeblScheduleDetailsComponent } from '../../pebl-schedule-details/pebl-schedule-details.component';
import { Subscription } from 'rxjs';

// Enough placeholder rows to fill a tall viewport; any excess is clipped.
const SKELETON_ROW_COUNT = 14;

@Component({
  standalone: false,
  selector: 'odin-pebl-scheduled-list',
  templateUrl: './pebl-scheduled-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./pebl-scheduled-list.component.scss'],
})
export class PeblScheduledListComponent
  implements OnInit, OnChanges, OnDestroy
{
  public rowSize = 62;

  // The CDK defaults (100px / 200px) leave barely a row and a half of runway,
  // so rows were still being created as they crossed the viewport edge — a
  // blank strip at the bottom on the way down and at the top on the way up.
  // Keep six rows rendered beyond each edge, topping back up to twelve.
  public minBufferPx = this.rowSize * 6;
  public maxBufferPx = this.rowSize * 12;

  public virtualScroll: CdkVirtualScrollViewport | null = null;
  private scrollSubscription: Subscription | null = null;

  // The viewport is replaced by the skeleton while the first page loads, so it
  // isn't in the DOM on first render. Binding the scroll listener from the
  // setter rather than ngAfterViewInit — which would fire once while the
  // viewport was still absent — keeps infinite scrolling working.
  @ViewChild(CdkVirtualScrollViewport, { static: false })
  set virtualScrollViewport(viewport: CdkVirtualScrollViewport | undefined) {
    const next = viewport ?? null;
    if (next === this.virtualScroll) return;

    this.scrollSubscription?.unsubscribe();
    this.scrollSubscription = null;
    this.virtualScroll = next;
    if (next == null) return;

    this.scrollSubscription = next
      .elementScrolled()
      .subscribe(() => this.CheckLoadMore());
  }

  // Shown instead of the (empty) viewport while the first page is loading.
  public skeletonRows = Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => i);
  public get showSkeleton(): boolean {
    return this.loading && this.schedules.length === 0;
  }

  @Input() statusFilter: PeblScheduleStatus | undefined;

  public schedules: ScheduleItem[] = [];
  public paginationToken: string = '';
  public loading: boolean = false;
  public fetched: boolean = false;
  public moreContent: boolean = true;

  private merchantChangeSub: Subscription;

  constructor(
    private vtService: VTService,
    private paginationHelper: PaginationHelperService,
    private dialog: MatDialog,
    private merchantChangeService: MerchantChangeService,
    private countryConvertService: CountryConvertService,
  ) {
    this.merchantChangeSub =
      this.merchantChangeService.merchantChangeEvent.subscribe(() => {
        this.RefreshSchedules();
      });
  }

  public ngOnInit(): void {
    this.FetchSchedules(this.paginationToken);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['statusFilter'] && !changes['statusFilter'].firstChange) {
      this.RefreshSchedules();
    }
  }

  ngOnDestroy(): void {
    this.scrollSubscription?.unsubscribe();
    if (this.merchantChangeSub !== undefined)
      this.merchantChangeSub.unsubscribe();
  }

  // Re-checks the scroll position and loads the next page if we're still
  // near the bottom. Called both on scroll events and after a fetch
  // completes, since a scroll event can fire (and get ignored) while a
  // fetch is already in flight - e.g. CDK virtual scroll adjusting the
  // viewport as rows are appended - and no further scroll event is
  // guaranteed to happen afterwards, which left pagination stuck until the
  // user scrolled again.
  private CheckLoadMore(): void {
    if (!this.virtualScroll || this.loading) return;
    if (this.paginationToken === undefined || this.paginationToken === '')
      return;

    const distFromBottom = this.virtualScroll.measureScrollOffset('bottom');
    this.paginationHelper.detectEndOfScrollRaw(
      distFromBottom,
      this.rowSize,
      15,
      () => this.FetchSchedules(this.paginationToken),
    );
  }

  private FetchSchedules(paginationToken?: string): void {
    if (this.loading) return;
    this.loading = true;
    this.vtService.GetSchedules(this.statusFilter, paginationToken).subscribe(
      (response: ScheduleListResponse) => {
        // Keep newest-first across appended pages
        this.schedules = [
          ...this.schedules,
          ...(response.schedules ?? []),
        ].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        this.paginationToken = response.lastEvaluatedKey;
        if (this.paginationToken === '') {
          this.moreContent = false;
        }
        this.loading = false;
        this.fetched = true;
        // CDK virtual scroll can fail to notice the new content height on
        // its own (e.g. growing from an empty list), leaving the results
        // unrendered until something else forces a relayout - so force one
        // straight away rather than leaving the spinner up over real results.
        this.virtualScroll?.checkViewportSize();
        this.CheckLoadMore();
      },
      () => {
        this.fetched = true;
        this.loading = false;
      },
    );
  }

  public RefreshSchedules(): void {
    this.schedules = [];
    this.paginationToken = '';
    this.moreContent = true;
    this.fetched = false;
    this.loading = false;
    this.FetchSchedules();
  }

  public OpenDetails(schedule: ScheduleItem): void {
    this.dialog
      .open(PeblScheduleDetailsComponent, {
        data: schedule,
        panelClass: ['main-panel', 'large-modal'],
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((didAction) => {
        if (didAction) this.RefreshSchedules();
      });
  }

  public rowTypeIcon(row: ScheduleItem): string {
    return row.type === 'Recurring' ? 'repeat' : 'event';
  }

  public rowTypeTooltip(row: ScheduleItem): string {
    return row.type === 'Recurring' ? 'Recurring schedule' : 'One-off send';
  }

  public rowRecipient(row: ScheduleItem): string {
    return row.emailAddress || row.phoneNumber || '-';
  }

  // The date column shows when the schedule will next send while it is still
  // running, and when it last sent once it has stopped.
  public rowDate(row: ScheduleItem): string | undefined {
    if (row.status === 'Active' || row.status === 'Sending')
      return row.nextSendAt;
    return row.lastSentAt;
  }

  public rowOccurrences(row: ScheduleItem): string {
    if (row.type !== 'Recurring') return '';
    return `${row.occurrencesSent}/${row.totalOccurrences ?? '-'}`;
  }

  public currencyAlphaCode(row: ScheduleItem): string {
    const countryData =
      this.countryConvertService.GetCountryByCurrencyNumericCode(
        row.currencyCode,
      );
    return countryData ? countryData.currencyCode : 'GBP';
  }

  public rowStatusPillClass(row: ScheduleItem): string {
    switch (row.status) {
      case 'Active':
        return 'text-white bg-success';
      case 'Sending':
        return 'text-black bg-warning';
      case 'Completed':
        return 'text-white bg-primary';
      case 'Cancelled':
        return 'text-white bg-secondary';
      case 'Failed':
        return 'text-white bg-danger';
    }
  }

  public rowStatusPillIcon(row: ScheduleItem): string {
    switch (row.status) {
      case 'Active':
        return 'schedule_send';
      case 'Sending':
        return 'outgoing_mail';
      case 'Completed':
        return 'check_circle';
      case 'Cancelled':
        return 'cancel';
      case 'Failed':
        return 'error';
    }
  }
}
